import React from 'react';
import { LatestBillWrapper } from 'modules/MyBills/components/LatestBill/styles';
import LatestBillFormItem from 'modules/MyBills/components/LatestBillFormItem/LatestBillFormItem';
import { Button, Icons, Tooltip } from '@myob/myob-widgets';
import InvoiceStatus from 'modules/MyBills/components/InvoiceStatus/InvoiceStatus';
import { downloadMyBillsPdf, EBillsStatus, IBill, IBillsState } from 'modules/MyBills';
import { EButtonNames, openPayNowUrlInNewTab, trackClickEventInMyBills } from 'modules/MyBills/myBillsHelper';
import { useHistory } from 'react-router-dom';
import { ERoutePath } from 'router';
import {
    EInvoiceFields,
    getTextOrColorForInvoice,
    getValueFromInvoice,
} from 'modules/MyBills/components/LatestBill/helper';
import { useRegionConfig } from 'hooks/useRegionConfig';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'stores';

export type Props = {
    latestBill: IBill | null;
};

export const LatestBill: React.FC<Props> = ({ latestBill }) => {
    const history = useHistory();
    const { PAY_INVOICE: payInvoiceUrl } = useRegionConfig();
    const { isDownloading } = useSelector<RootState, IBillsState>(state => state.bills);

    const dispatch = useDispatch();

    const handleDownloadBill = async (invoice: IBill) => {
        trackClickEventInMyBills(
            invoice?.status === EBillsStatus.Credited ? EButtonNames.ViewNotice : EButtonNames.DownloadBill,
            'LatestBill',
        );
        dispatch(downloadMyBillsPdf(invoice?.invoiceNumber));
    };
    const handleClickUpdatePaymentLink = () => {
        trackClickEventInMyBills(EButtonNames.UpdatePaymentDetails, 'LatestBill');
        history.push(ERoutePath.PaymentProfile);
    };

    const handlePayNow = () => {
        trackClickEventInMyBills(EButtonNames.PayNow, 'LatestBill');
        latestBill && openPayNowUrlInNewTab(payInvoiceUrl, latestBill.invoiceNumber);
    };

    return (
        <LatestBillWrapper>
            <div className="forms">
                <p data-testid="status">
                    Your latest bill &nbsp; <InvoiceStatus status={latestBill?.status} />
                    {latestBill?.status === EBillsStatus.DueNow && (
                        <Tooltip className="due-now-tooltip" trigger="click">
                            It may take 3-5 business days for your payments to appear
                        </Tooltip>
                    )}
                </p>
                <div className="period">
                    <LatestBillFormItem title="BILLING PERIOD">
                        <div className="period" data-testid="billPeriod">
                            {getValueFromInvoice(latestBill, EInvoiceFields.Period)}
                        </div>
                    </LatestBillFormItem>
                </div>
                <div className="number">
                    <LatestBillFormItem title="INVOICE NUMBER">
                        <div className="invoice-number" data-testid="billInvoiceNumber">
                            {getValueFromInvoice(latestBill, EInvoiceFields.InvoiceNumber)}
                        </div>
                    </LatestBillFormItem>
                </div>
                <div className="next">
                    <LatestBillFormItem title={getTextOrColorForInvoice(latestBill, EInvoiceFields.IssuedTitle)}>
                        <div
                            className={`next-bill ${getTextOrColorForInvoice(
                                latestBill,
                                EInvoiceFields.IssuedTitleColor,
                            )}`}
                            data-testid="billNextBill"
                        >
                            {getValueFromInvoice(latestBill, EInvoiceFields.Issued)}
                        </div>
                    </LatestBillFormItem>
                </div>
                <div className="amount">
                    <LatestBillFormItem title={getTextOrColorForInvoice(latestBill, EInvoiceFields.AmountTitle)}>
                        <div
                            className={`amount-total ${getTextOrColorForInvoice(
                                latestBill,
                                EInvoiceFields.AmountColor,
                            )}`}
                            data-testid="billAmount"
                        >
                            {getValueFromInvoice(latestBill, EInvoiceFields.Amount)}
                        </div>
                    </LatestBillFormItem>
                </div>
            </div>
            <div className="btns">
                <Button type="primary" onClick={handlePayNow} disabled={latestBill?.status !== EBillsStatus.DueNow}>
                    Pay now
                </Button>
                <Button
                    type="secondary"
                    icon={<Icons.Download />}
                    disabled={latestBill?.status === undefined || isDownloading}
                    onClick={() => handleDownloadBill(latestBill as IBill)}
                >
                    {getTextOrColorForInvoice(latestBill, EInvoiceFields.DownloadButton)}
                </Button>
                {latestBill?.status === EBillsStatus.DueNow && (
                    <Button type="link" onClick={handleClickUpdatePaymentLink}>
                        Update payment details
                    </Button>
                )}
            </div>
        </LatestBillWrapper>
    );
};

export default LatestBill;
